import {Icon, LinkText, Tooltip} from '@/components/atoms'
import ShimmerTableRow from '@/components/atoms/ShimmerTableRow'
import EmptyState from '@/components/molecules/EmptyState/EmptyState'
import {useCustomDataTable} from '@/hooks/useCustomDataTable-hook'
import {COLOR_CODE} from '@/utils/constants/colors'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material'
import {useState} from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import './customDataTable.scss'

// Design tokens for consistent styling
const TABLE_CELL_PADDING = '8px'
const HEADER_FONT_SIZE = '12px'
const CONTENT_FONT_SIZE = '12px'
const INPUT_FONT_SIZE = '12px'
const FILTER_INPUT_HEIGHT = '24px'
const BUTTON_FONT_SIZE = '14px'
const PAGINATION_FONT_SIZE = '14px'

const CustomDataTable = ({
  columns = [],
  records = [],
  isLoading = false,
  pageSize = 10,
  totalRecords = 0,
  setPageIndex = () => {},
  handleSorting = () => {},
  setSearchArry = () => {},
  onResetAll = () => {},
  onRowSelect = () => {},
  actions = [],
  showActions = false,
  isSelectAll = false,
  showResetAll = true,
  enableSelection = true,
  showColumnFilters = true,
  enableSorting = true,
  isExpandable = false,
  renderSubComponent = () => null,
  id = 'uuid',
  initialSelectedRows = [],
  tableHeader = '',
  SecondaryBtnText = '',
  onSecondaryBtnClick = () => {},
  secondaryBtnStartIcon = '',
  copyIconClick = () => {},
  setSelectAll = () => {},
  showFooter = true,
  defaultSortConfig = {key: null, direction: 'desc'},
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [expandedRows, setExpandedRows] = useState([])
  const open = Boolean(anchorEl)
  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleActionClick = action => {
    const isDisabled = typeof action.disabled === 'function' ? action.disabled({selectedRows}) : action.disabled

    if (action.onClick && !isDisabled) {
      if (typeof action.onClick === 'function') {
        action.onClick({selectedRows})
      } else {
        action.onClick()
      }
    }
    handleMenuClose()
  }

  const {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    page,
    setSortConfig,
    sortConfig,
    filter,
    totalPages,
    setOpenDatePicker,
    handleResetAll,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handlePageChange,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
  } = useCustomDataTable({
    columns,
    records,
    pageSize,
    onResetAll,
    onRowSelect,
    handleSorting,
    setSearchArry,
    setPageIndex,
    isSelectAll,
    totalRecords,
    initialSelectedRows,
    setSelectAll,
    id,
    defaultSortConfig,
  })

  const CustomInput = ({value = '', onClick, column}) => {
    if (!column?.name_of_db_column) return null

    const dateRange = dateRanges[column.name_of_db_column] || {}
    const {startDate, endDate} = dateRange
    const showCross = startDate && endDate
    const showCalendar = !showCross

    const buttonText =
      startDate && endDate
        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
        : startDate
          ? `${startDate.toLocaleDateString()} - End Date`
          : value || column.label_of_column

    return (
      <Button
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          setOpenDatePicker(column.name_of_db_column)
          onClick && onClick(e)
        }}
        variant="outlined"
        size="small"
        endIcon={showCalendar ? <Icon icon="calendar_today" size="small" /> : null}
        fullWidth
        sx={{
          color: startDate || endDate ? COLOR_CODE._EFEFEF : COLOR_CODE._616161,
          borderColor: 'rgba(255, 255, 255, 0.23)',
          backgroundColor: '#272727',
          width: '100%',
          minWidth: '120px',
          height: FILTER_INPUT_HEIGHT,
          padding: '8px 10px',
          fontSize: INPUT_FONT_SIZE,
          display: 'flex',
          textTransform: 'none',
          justifyContent: 'space-between',
          '& .MuiButton-endIcon': {
            margin: 0,
          },
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
        }}
      >
        {buttonText}
      </Button>
    )
  }

  const SingleDateCustomInput = ({value = '', onClick, column}) => {
    if (!column?.name_of_db_column) return null

    const selectedDate = singleDates[column.name_of_db_column]

    return (
      <Button
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          setOpenDatePicker(column.name_of_db_column)
          onClick && onClick(e)
        }}
        variant="outlined"
        size="small"
        endIcon={!selectedDate ? <Icon icon="calendar_today" size="small" /> : null}
        fullWidth
        sx={{
          color: selectedDate ? COLOR_CODE._EFEFEF : COLOR_CODE._616161,
          borderColor: 'rgba(255, 255, 255, 0.23)',
          backgroundColor: '#272727',
          width: '100%',
          minWidth: '150px',
          height: FILTER_INPUT_HEIGHT,
          padding: '8px 10px',
          fontSize: INPUT_FONT_SIZE,
          justifyContent: 'space-between',
          textTransform: 'none',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
        }}
      >
        {selectedDate ? selectedDate.toLocaleDateString() : value || column.label_of_column}
      </Button>
    )
  }

  // Toggle row expansion
  const handleToggleExpand = rowKey => {
    setExpandedRows(prev => (prev.includes(rowKey) ? prev.filter(k => k !== rowKey) : [...prev, rowKey]))
  }

  return (
    <div className="App dark-mode" style={{backgroundColor: COLOR_CODE._1D1D1D}} data-testid={id || undefined}>
      {tableHeader && <div className="data-table-header">{tableHeader}</div>}
      <Box
        sx={{
          color: COLOR_CODE._3387DF,
          paddingLeft: '8px',
          paddingY: '16px',
          fontSize: CONTENT_FONT_SIZE,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {showResetAll && (
          <Button
            onClick={() => {
              setSortConfig(defaultSortConfig)
              handleResetAll()
            }}
            variant="text"
            size="small"
            sx={{
              color: COLOR_CODE._3387DF,
              fontSize: BUTTON_FONT_SIZE,
              textTransform: 'none',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.8,
              },
            }}
          >
            Reset All
          </Button>
        )}
        {showActions && actions.length > 0 && (
          <div>
            {SecondaryBtnText && (
              <Button
                onClick={onSecondaryBtnClick}
                variant="text"
                size="small"
                sx={{
                  color: COLOR_CODE._3387DF,
                  fontSize: BUTTON_FONT_SIZE,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  marginRight: '20px',
                  borderColor: COLOR_CODE._3387DF,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderRadius: '60px',
                  paddingX: '16px',
                  '&:hover': {
                    borderColor: COLOR_CODE._3387DF,
                    backgroundColor: 'rgba(51, 135, 223, 0.04)',
                  },
                }}
                startIcon={<Icon className="!text-[#0268d7]" icon={secondaryBtnStartIcon} size="small" />}
              >
                {SecondaryBtnText}
              </Button>
            )}
            <Button
              onClick={handleMenuClick}
              variant="outlined"
              size="small"
              endIcon={<Icon icon="expand_more" size="small" color={COLOR_CODE._3387DF} />}
              sx={{
                color: COLOR_CODE._3387DF,
                fontSize: BUTTON_FONT_SIZE,
                textTransform: 'none',
                fontWeight: 'bold',
                marginRight: '20px',
                borderColor: COLOR_CODE._3387DF,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderRadius: '60px',
                paddingX: '16px',
                '&:hover': {
                  borderColor: COLOR_CODE._3387DF,
                  backgroundColor: 'rgba(51, 135, 223, 0.04)',
                },
              }}
            >
              Actions
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiPaper-root': {
                  backgroundColor: COLOR_CODE._1D1D1D,
                  border: `1px solid #4E4E4E`,
                  borderRadius: '4px',
                  marginTop: '8px',
                  minWidth: '220px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  overflow: 'hidden',
                  '& .MuiList-root': {
                    padding: '0',
                  },
                  paddingLeft: '4px',
                  paddingRight: '4px',
                },
              }}
            >
              {actions.map((action, index) => {
                if (action.divider) {
                  return (
                    <Divider
                      key={`divider-${index}`}
                      sx={{
                        backgroundColor: COLOR_CODE._383838,
                        margin: 0,
                      }}
                    />
                  )
                }

                const isDisabled =
                  typeof action.disabled === 'function' ? action.disabled({selectedRows}) : action.disabled

                return (
                  <MenuItem
                    key={action.id || `action-${index}`}
                    onClick={() => handleActionClick(action)}
                    disabled={isDisabled}
                    sx={{
                      color: isDisabled ? COLOR_CODE._616161 : '#D0D0D0',
                      fontSize: CONTENT_FONT_SIZE,
                      fontWeight: 400,
                      padding: '4px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      '&:hover': {
                        backgroundColor: isDisabled ? 'transparent' : COLOR_CODE._272727,
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      },
                      borderBottom: '1px solid #4E4E4E',
                    }}
                  >
                    {action.icon && (
                      <Icon
                        icon={action.icon}
                        size="small"
                        color={isDisabled ? COLOR_CODE._616161 : COLOR_CODE._FFFFFF}
                      />
                    )}
                    {action.label}
                  </MenuItem>
                )
              })}
            </Menu>
          </div>
        )}
      </Box>
      {enableSelection && (
        <Box
          sx={{
            paddingLeft: '8px',
            backgroundColor: COLOR_CODE._1D1D1D,
            color: COLOR_CODE._EFEFEF,
            fontSize: CONTENT_FONT_SIZE,
            marginBottom: '8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isSelectAll ? totalRecords : selectedRows?.length} item(s) selected
        </Box>
      )}
      <TableContainer
        className="dark-mode-table table-scroll-container"
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
            backgroundColor: COLOR_CODE._1D1D1D,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLOR_CODE._616161,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: COLOR_CODE._818181,
            },
          },
        }}
      >
        <Table sx={{tableLayout: 'auto'}}>
          <TableHead>
            <TableRow>
              {(enableSelection || isExpandable) && (
                <TableCell
                  sx={{
                    width: 'auto',
                    minWidth: 'fit-content',
                    borderBottom: '2px solid rgba(81, 81, 81, 1)',
                    padding: TABLE_CELL_PADDING,
                    backgroundColor: COLOR_CODE._1D1D1D,
                  }}
                >
                  {enableSelection ? (
                    <Checkbox
                      size="small"
                      checked={
                        isSelectAll ||
                        (records?.length > 0 &&
                          records.every(record => {
                            const hasMatch = selectedRows.some(selected => {
                              const selectedValue = selected[id]
                              const recordValue = record[id]
                              const isEqual = selectedValue === recordValue

                              return isEqual
                            })

                            return hasMatch
                          }))
                      }
                      onChange={handleSelectAll}
                      indeterminate={!isSelectAll && selectedRows.length > 0 && selectedRows.length < records.length}
                    />
                  ) : null}
                </TableCell>
              )}
              {columns.map((column, columnIndex) => (
                <TableCell
                  key={`header-${columnIndex}`}
                  align="left"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    backgroundColor: COLOR_CODE._1D1D1D,
                    cursor: 'default',
                    borderBottom: '2px solid rgba(81, 81, 81, 1)',
                    padding: TABLE_CELL_PADDING,
                  }}
                >
                  {enableSorting ? (
                    <TableSortLabel
                      hideSortIcon={true}
                      active={sortConfig.key === column.name_of_db_column}
                      direction={
                        sortConfig.key === column.name_of_db_column ? sortConfig.direction || 'asc' : undefined
                      }
                      onClick={() => handleSort(column.name_of_db_column)}
                      disableRipple
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: 1,
                        },
                        '&.MuiTableSortLabel-root': {
                          backgroundColor: COLOR_CODE._1D1D1D,
                          color: COLOR_CODE._EFEFEF,
                          fontSize: HEADER_FONT_SIZE,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          '&:hover': {
                            color: 'inherit',
                          },
                        },
                      }}
                    >
                      {column.label_of_column}
                    </TableSortLabel>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: COLOR_CODE._1D1D1D,
                        color: COLOR_CODE._EFEFEF,
                        fontSize: HEADER_FONT_SIZE,
                        fontWeight: 'bold',
                      }}
                    >
                      {column.label_of_column}
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
            {showColumnFilters && (
              <TableRow>
                {(enableSelection || isExpandable) && (
                  <TableCell
                    sx={{
                      backgroundColor: COLOR_CODE._1D1D1D,
                      borderBottom: '2px solid rgba(81, 81, 81, 1)',
                      padding: TABLE_CELL_PADDING,
                      width: 'auto',
                      minWidth: 'fit-content',
                      '&:hover': {
                        backgroundColor: COLOR_CODE._1D1D1D,
                      },
                    }}
                  />
                )}
                {columns.map((column, index) => (
                  <TableCell
                    key={`input-row${index}`}
                    sx={{
                      backgroundColor: COLOR_CODE._1D1D1D,
                      borderBottom: '2px solid rgba(81, 81, 81, 1)',
                      padding: TABLE_CELL_PADDING,
                      '&:hover': {
                        backgroundColor: COLOR_CODE._1D1D1D,
                      },
                    }}
                  >
                    {column.filterVariant === 'dateRange' ? (
                      <DatePicker
                        selectsRange={true}
                        startDate={dateRanges[column.name_of_db_column]?.startDate || null}
                        endDate={dateRanges[column.name_of_db_column]?.endDate || null}
                        onChange={dates => handleDateRangeChange(dates, column.name_of_db_column)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Select date range"
                        isClearable={
                          dateRanges[column.name_of_db_column]?.startDate &&
                          dateRanges[column.name_of_db_column]?.endDate
                        }
                        customInput={<CustomInput column={column} />}
                        open={openDatePicker === column.name_of_db_column}
                        onCalendarOpen={() => setOpenDatePicker(column.name_of_db_column)}
                        onCalendarClose={() => setOpenDatePicker('')}
                        peekNextMonth
                        showMonthDropdown
                        useShortMonthInDropdown
                        showYearDropdown
                        dateFormatCalendar="MMMM"
                        yearDropdownItemNumber={15}
                        scrollableYearDropdown
                        dropdownMode="select"
                        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 30))}
                        maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 200))}
                      />
                    ) : column.filterVariant === 'date' ? (
                      <DatePicker
                        selected={singleDates[column.name_of_db_column] || null}
                        onChange={date => handleSingleDateChange(date, column.name_of_db_column)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Select date"
                        isClearable={true}
                        customInput={<SingleDateCustomInput column={column} />}
                        open={openDatePicker === column.name_of_db_column}
                        onCalendarOpen={() => setOpenDatePicker(column.name_of_db_column)}
                        onCalendarClose={() => setOpenDatePicker('')}
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={230}
                        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 30))}
                        maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 200))}
                      />
                    ) : (
                      <TextField
                        size="small"
                        name={column.name_of_db_column}
                        value={filter[column.name_of_db_column] || ''}
                        onChange={handleFilterChange}
                        placeholder={`${column.label_of_column}`}
                        sx={{
                          width: '100%',
                          minWidth: '120px',
                          '& .MuiInputBase-input': {
                            color: COLOR_CODE._EFEFEF,
                            padding: '8px 10px',
                            fontSize: INPUT_FONT_SIZE,
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: `${COLOR_CODE._616161} !important`,
                            opacity: 1,
                            fontSize: INPUT_FONT_SIZE,
                          },
                          '& .MuiOutlinedInput-root': {
                            height: FILTER_INPUT_HEIGHT,
                            backgroundColor: '#272727',
                            border: 'none',
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.23)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.5)',
                            },
                          },
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({length: 12}).map((_, index) => (
                  <ShimmerTableRow key={`loading-${index}`} columnCount={columns.length + 1} />
                ))
              : null}
            {!isLoading && records?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{
                    backgroundColor: COLOR_CODE._1D1D1D,
                    '&:hover': {
                      backgroundColor: COLOR_CODE._1D1D1D,
                    },
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: COLOR_CODE._1D1D1D,
                    },
                  }}
                >
                  <EmptyState onActionClick={handleResetAll} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading
              ? records?.map((row, rowIndex) => {
                  // Ensure we always have a stable, unique key for each row. If the primary `id` column
                  // (or `row.id`) is missing/undefined, fall back to the rowIndex. This prevents
                  // scenarios where `undefined` is used for every row, which caused all rows to expand/contract together.
                  const uniqueRowKey = row[id] ?? row.id ?? rowIndex

                  return (
                    <>
                      <TableRow
                        key={`row-${uniqueRowKey}`}
                        selected={selectedRows.some(selectedRow => selectedRow[id] === row[id])}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: `${COLOR_CODE._383838} !important`,
                          },
                          '&:hover': {
                            backgroundColor: COLOR_CODE._383838,
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: '2px solid rgba(81, 81, 81, 1)',
                            padding: TABLE_CELL_PADDING,
                          },
                        }}
                      >
                        {(enableSelection || isExpandable) && (
                          <TableCell>
                            {enableSelection ? (
                              <Checkbox
                                size="small"
                                checked={selectedRows.some(
                                  selectedRow => selectedRow[id] === row[id] && selectedRow.id === row.id
                                )}
                                onChange={() => handleRowSelect(row[id] || row.id)}
                              />
                            ) : (
                              isExpandable &&
                              renderSubComponent({original: row}) && (
                                <>
                                  <Icon
                                    data-testid={`expand-row-${rowIndex}`}
                                    icon={expandedRows.includes(uniqueRowKey) ? 'expand_less' : 'expand_more'}
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation()
                                      handleToggleExpand(uniqueRowKey)
                                    }}
                                    style={{cursor: 'pointer', justifyContent: 'center'}}
                                  />
                                </>
                              )
                            )}
                          </TableCell>
                        )}
                        {columns.map((column, colIndex) => {
                          const cellValue = row[column.name_of_db_column]
                          const isDateFilter = column.filterVariant === 'date'
                          const displayValue =
                            isDateFilter && cellValue ? `${cellValue?.split('T')[0]}` : cellValue || '—'

                          return (
                            <TableCell key={`cell-${colIndex}`} align="left">
                              {column?.renderCell ? (
                                column.renderCell(cellValue, row, column)
                              ) : column?.isClickable && column?.isIconShow ? (
                                <div
                                  role="button"
                                  onClick={() => copyIconClick(row)}
                                  className="data-table-icon"
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault()
                                      copyIconClick(row)
                                    }
                                  }}
                                  tabIndex={0}
                                >
                                  <span>{displayValue}</span>
                                  <Tooltip direction="left" text={column?.iconTooltip || ''}>
                                    <Icon className="cursor-pointer !text-[#0268d7]" icon={'copy'} size="big" />
                                  </Tooltip>
                                </div>
                              ) : column?.isClickable ? (
                                <LinkText
                                  text={displayValue}
                                  id={`link-${rowIndex}-${column.name_of_db_column}`}
                                  handleClick={() => {
                                    column.onCellClick && column.onCellClick(cellValue, row, column)
                                  }}
                                />
                              ) : (
                                displayValue
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                      {isExpandable && renderSubComponent({original: row}) && expandedRows.includes(uniqueRowKey) && (
                        <TableRow key={`sub-${uniqueRowKey}`} sx={{backgroundColor: COLOR_CODE._1D1D1D}}>
                          <TableCell />
                          <TableCell
                            colSpan={columns.length + 1}
                            sx={{borderBottom: '2px solid rgba(81, 81, 81, 1)', padding: TABLE_CELL_PADDING}}
                          >
                            {renderSubComponent({original: row})}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '16px',
          marginBottom: '16px',
          paddingX: '20px',
        }}
      >
        <Box sx={{width: '100px'}} /> {/* Spacer for balance */}
        {showFooter ? (
          <>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              boundaryCount={0}
              sx={{
                '& .MuiPagination-ul': {
                  gap: 0,
                },
                '& .MuiPaginationItem-root': {
                  backgroundColor: 'transparent',
                  color: COLOR_CODE._818181 || '#818181',
                  border: `1px solid ${COLOR_CODE._515151 || '#515151'}`,
                  borderRadius: '4px',
                  minWidth: '32px',
                  height: '32px',
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: COLOR_CODE._383838 || '#383838',
                    borderColor: COLOR_CODE._616161 || '#616161',
                  },
                  '&.Mui-selected': {
                    backgroundColor: COLOR_CODE._3387DF || '#3387DF',
                    color: COLOR_CODE._FFFFFF || '#FFFFFF',
                    borderColor: COLOR_CODE._3387DF || '#3387DF',
                    '&:hover': {
                      backgroundColor: COLOR_CODE._2878D0 || '#2878D0',
                      borderColor: COLOR_CODE._2878D0 || '#2878D0',
                    },
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    backgroundColor: COLOR_CODE._1D1D1D || '#1D1D1D',
                    color: COLOR_CODE._515151 || '#515151',
                  },
                },
                '& .MuiPaginationItem-page': {
                  borderRadius: 0,
                  minWidth: '39px',
                  fontSize: PAGINATION_FONT_SIZE,
                },
                '& .MuiPaginationItem-previousNext': {
                  color: COLOR_CODE._3387DF || '#3387DF',
                  '&:hover': {
                    backgroundColor: COLOR_CODE._383838 || '#383838',
                  },
                },
                '& li:first-child .MuiPaginationItem-previousNext': {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                '& li:last-child .MuiPaginationItem-previousNext': {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                },
                '& .MuiPaginationItem-firstLast': {
                  display: 'none', // Hide first/last buttons to match the design
                },
                '& .MuiPaginationItem-ellipsis': {
                  display: 'none',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: COLOR_CODE._818181 || '#818181',
                },
              }}
            />
            <Box
              sx={{
                color: COLOR_CODE._EFEFEF,
                fontSize: PAGINATION_FONT_SIZE,
                fontWeight: 500,
                marginLeft: '10px',
              }}
            >
              {totalRecords} total items
            </Box>
          </>
        ) : null}
      </Box>
    </div>
  )
}

export default CustomDataTable
